// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (owner on Quest, 2026-11-04: "When the user has a bigger than 2m
 * sphere and they go inside they should be moved to the center of it.
 * Satellites should be disabled there and the dialogs from data, layers,
 * etc should go inside the globe."): the view from inside a big globe.
 *
 *   - It starts when the globe is more than 2 m across and the eyes are
 *     inside it, however they got there (the left stick, two hands, a
 *     lead's size, a walk). A headset cannot move the viewer, so the globe
 *     glides until its centre is at the eyes, and holds there.
 *   - It ends when the globe is made smaller than 1.9 m (the gap keeps it
 *     from flickering at 2 m) or the viewer walks out through its wall.
 *     Made smaller, the globe glides back where it stood before (a placed
 *     globe to its placement), or in front of the viewer when they would
 *     still be inside it there. Walked out of, it stays where it is.
 *   - Meanwhile nothing else moves it: a placement's anchor waits, and the
 *     grip does not carry it. Satellites are hidden. The dock, the date
 *     track, the side panel, the legend, the Khipu panel and the small
 *     panels stand around the viewer inside the wall, and turn with them
 *     once they look more than 60° away.
 *
 * Pure: plain numbers in and out, tested without a headset
 * (`vrInsideView.test.ts`); the session (`vrSession`) applies each step.
 */

export interface Vec3 {
  x: number
  y: number
  z: number
}

export const INSIDE_ENTER_DIAMETER_M = 2
export const INSIDE_LEAVE_DIAMETER_M = 1.9
/** The eyes count as inside this far within the wall, and as out this far beyond it. */
const WALL_MARGIN_M = 0.03
/** The globe's glide to the eyes and back. */
export const INSIDE_GLIDE_MS = 600
/** The panels' move between their two places. */
export const INSIDE_BLEND_MS = 400
/** The panels turn with the viewer once they look this far away, until they are back within FOLLOW_SETTLE_RAD. */
export const FOLLOW_START_RAD = (60 * Math.PI) / 180
const FOLLOW_SETTLE_RAD = (5 * Math.PI) / 180
/** How fast they turn meanwhile: the gap shrinks by e every 1/FOLLOW_RATE seconds. */
const FOLLOW_RATE = 4
/** Sent out in front of the viewer, the globe leaves this much air between them and its wall. */
const OUT_FRONT_GAP_M = 0.5

/** Where the dock stands inside: ahead of the eyes and below them, as far as it is outside. */
export const INSIDE_DOCK_AHEAD_M = 0.72
export const INSIDE_DOCK_BELOW_M = 0.46
/** The side panel to the right, the legend and the Khipu panel to the left, this far round from ahead. */
const SIDE_ANGLE_RAD = (40 * Math.PI) / 180
const LEGEND_ANGLE_RAD = (-36 * Math.PI) / 180
const KHIPU_ANGLE_RAD = (-40 * Math.PI) / 180
/** AYNI (owner, 2026-11-04): the chatbot's window at the viewer's side, past the legend and the Khipu panel. */
const CHAT_ANGLE_RAD = (-68 * Math.PI) / 180

export type InsidePhase = 'outside' | 'entering' | 'inside' | 'leaving'

export interface InsideInput {
  /** Milliseconds, any clock that only goes forward. */
  now: number
  eye: Vec3
  /** Where the eyes look, any length; only its level part counts. */
  gaze: Vec3
  /** The globe's centre as the other writers left it this frame. */
  centre: Vec3
  /** The globe's radius now, metres. */
  radius: number
  /** A placement holds the globe (its anchor stands down while inside). */
  placed: boolean
}

export interface InsideStep {
  phase: InsidePhase
  /** Where the globe's centre goes this frame, or null to leave it to the other writers. */
  centre: Vec3 | null
  /** Let the placement go: the globe stays where it now is (walked out of it, or sent in front). */
  releasePlacement: boolean
  /** 0 outside … 1 inside: how far the panels are into their places around the viewer. */
  blend: number
  /** The way the panels face from the viewer, level and of length 1. */
  heading: { x: number; z: number }
  /** It went inside on this frame (the session says how to come out). */
  entered: boolean
}

/** The panels' places inside, around the eyes. */
export interface InsideLayout {
  dock: Vec3
  side: Vec3
  legend: Vec3
  khipu: Vec3
  /** The AYNI chatbot's window (vrChatPanel), at the viewer's side. */
  chat: Vec3
  /** For the small panels: a point ahead, the viewer's right and the way back toward them. */
  frame: { centre: Vec3; right: Vec3; toward: Vec3 }
}

/** The small panels' frame inside, and how far into it they are. */
export interface InsideFrame {
  centre: Vec3
  right: Vec3
  toward: Vec3
  blend: number
}

function dist(a: Vec3, b: Vec3): number {
  return Math.hypot(a.x - b.x, a.y - b.y, a.z - b.z)
}

function lerp(a: Vec3, b: Vec3, k: number): Vec3 {
  return { x: a.x + (b.x - a.x) * k, y: a.y + (b.y - a.y) * k, z: a.z + (b.z - a.z) * k }
}

/** Ease in and out (cubic). */
function ease(k: number): number {
  const c = Math.min(1, Math.max(0, k))
  return c < 0.5 ? 4 * c * c * c : 1 - Math.pow(-2 * c + 2, 3) / 2
}

/** The gaze laid flat and of length 1, or null when it looks almost straight up or down. */
export function levelGaze(gaze: Vec3): { x: number; z: number } | null {
  const len = Math.hypot(gaze.x, gaze.z)
  // Looking up at the pole or down at the floor turns no panel.
  if (len < 0.3 * Math.hypot(gaze.x, gaze.y, gaze.z) || len < 1e-6) return null
  return { x: gaze.x / len, z: gaze.z / len }
}

/** The signed angle from `a` to `b` round the vertical, positive to the right (both level, length 1). */
function turnTo(a: { x: number; z: number }, b: { x: number; z: number }): number {
  // Right of a heading (x, z) is (-z, x) in a room whose forward is -z.
  return Math.atan2(a.x * b.z - a.z * b.x, a.x * b.x + a.z * b.z)
}

function rotate(h: { x: number; z: number }, angle: number): { x: number; z: number } {
  // Positive turns to the viewer's right: right of (x, z) is (-z, x).
  const c = Math.cos(angle)
  const s = Math.sin(angle)
  return { x: h.x * c - h.z * s, z: h.z * c + h.x * s }
}

/** How far ahead the side panels stand inside a globe of `radius`: within its wall, never closer than a reach. */
export function insideReach(radius: number): number {
  return Math.min(1.25, Math.max(0.8, radius - 0.12))
}

/** The panels' places inside, round the eyes facing `heading`. */
export function insideLayout(eye: Vec3, heading: { x: number; z: number }, radius: number): InsideLayout {
  const reach = insideReach(radius)
  const at = (angle: number, d: number, dy: number): Vec3 => {
    const dir = rotate(heading, angle)
    return { x: eye.x + dir.x * d, y: eye.y + dy, z: eye.z + dir.z * d }
  }
  const right = rotate(heading, Math.PI / 2)
  return {
    dock: at(0, INSIDE_DOCK_AHEAD_M, -INSIDE_DOCK_BELOW_M),
    side: at(SIDE_ANGLE_RAD, reach, 0.03),
    legend: at(LEGEND_ANGLE_RAD, reach * 0.9, -0.12),
    khipu: at(KHIPU_ANGLE_RAD, reach, 0.22),
    chat: at(CHAT_ANGLE_RAD, reach, -0.02),
    frame: {
      centre: at(0, reach, 0),
      right: { x: right.x, y: 0, z: right.z },
      toward: { x: -heading.x, y: 0, z: -heading.z },
    },
  }
}

/**
 * Where a globe made smaller from inside goes: back to `before` when the
 * viewer stands clear of it there, else in front of them with air between
 * them and its wall.
 */
export function leavePosition(eye: Vec3, heading: { x: number; z: number }, radius: number, before: Vec3): Vec3 {
  if (dist(eye, before) >= radius + 0.25) return { ...before }
  const d = radius + OUT_FRONT_GAP_M
  return { x: eye.x + heading.x * d, y: eye.y, z: eye.z + heading.z * d }
}

export interface InsideView {
  /**
   * Before the placement's anchor writes the globe, at the start of the
   * frame's position work: where to put the globe back so it writes from
   * there (null: leave it). `blocked`: Place mode, a marker scan or a carry
   * owns the globe this frame, and the inside view stands down at once.
   */
  begin(blocked: boolean): Vec3 | null
  /** The anchor waits this frame. */
  holdsPlacement(): boolean
  /** After the anchor: the step to apply. */
  step(input: InsideInput): InsideStep
  phase(): InsidePhase
  /** Satellites stand down. */
  hidesSatellites(): boolean
}

export function createInsideView(): InsideView {
  let phase: InsidePhase = 'outside'
  /** Where the globe stood before going inside, and where it holds inside (the eyes then). */
  let before: Vec3 | null = null
  let hold: Vec3 | null = null
  /** The glide under way: from where, since when, and (leaving) where to. */
  let glideFrom: Vec3 | null = null
  let glideStart = 0
  let target: Vec3 | null = null
  let heading = { x: 0, z: -1 }
  let following = false
  let blend = 0
  let blocked = false
  let lastNow: number | null = null

  function toOutside(): void {
    phase = 'outside'
    before = null
    hold = null
    glideFrom = null
    target = null
    following = false
  }

  return {
    begin(isBlocked) {
      blocked = isBlocked
      if (blocked && phase !== 'outside') toOutside()
      // Leaving, the globe goes back to where it is going before the anchor
      // writes, so a frame without an anchor pose (which writes the height
      // only) builds on that rather than on the glide's last step.
      return phase === 'leaving' ? target : null
    },

    holdsPlacement() {
      return phase === 'entering' || phase === 'inside'
    },

    step(input) {
      const dt = lastNow === null ? 0 : Math.max(0, input.now - lastNow)
      lastNow = input.now
      // The time since the last frame was spent in the phase it ended in.
      const wasInside = phase !== 'outside'
      const diameter = input.radius * 2
      const look = levelGaze(input.gaze)
      let entered = false
      let releasePlacement = false
      let centre: Vec3 | null = null

      if (phase === 'outside' && !blocked && diameter > INSIDE_ENTER_DIAMETER_M && dist(input.eye, input.centre) < input.radius - WALL_MARGIN_M) {
        phase = 'entering'
        entered = true
        before = { ...input.centre }
        hold = { ...input.eye }
        glideFrom = { ...input.centre }
        glideStart = input.now
        heading = look ?? heading
        following = false
      }

      if (phase === 'entering' || phase === 'inside') {
        const k = (input.now - glideStart) / INSIDE_GLIDE_MS
        // Where it is drawn now: on its way to the eyes, or there.
        const shown = phase === 'inside' ? { ...hold! } : lerp(glideFrom!, hold!, ease(k))
        if (diameter < INSIDE_LEAVE_DIAMETER_M) {
          // Made smaller: back where it stood, or out in front of the viewer.
          const going = leavePosition(input.eye, look ?? heading, input.radius, before!)
          // Sent in front, a placement would pull it back: it is let go.
          if (input.placed && dist(going, before!) > 1e-9) releasePlacement = true
          phase = 'leaving'
          glideFrom = shown
          glideStart = input.now
          target = going
          centre = shown
        } else if (phase === 'inside' && dist(input.eye, hold!) > input.radius + WALL_MARGIN_M) {
          // Walked out through the wall: it stays where it is.
          centre = shown
          if (input.placed) releasePlacement = true
          toOutside()
        } else {
          if (phase === 'entering' && k >= 1) phase = 'inside'
          centre = phase === 'inside' ? { ...hold! } : shown
        }
      } else if (phase === 'leaving') {
        // Toward where it stood; a placement's anchor has just written that afresh.
        const going = input.placed ? { ...input.centre } : { ...target! }
        target = going
        const k = (input.now - glideStart) / INSIDE_GLIDE_MS
        centre = k >= 1 ? going : lerp(glideFrom!, going, ease(k))
        if (k >= 1) toOutside()
      }

      // The panels turn with the viewer once they look far away from them.
      if (phase === 'entering' || phase === 'inside') {
        if (look) {
          const off = turnTo(heading, look)
          if (Math.abs(off) > FOLLOW_START_RAD) following = true
          if (following) {
            const k = 1 - Math.exp(-FOLLOW_RATE * (dt / 1000))
            heading = rotate(heading, off * k)
            const n = Math.hypot(heading.x, heading.z) || 1
            heading = { x: heading.x / n, z: heading.z / n }
            if (Math.abs(off * (1 - k)) < FOLLOW_SETTLE_RAD) following = false
          }
        }
      }
      // In from the start of the glide; out only once the globe has settled
      // where it goes, so the panels never stand by a direction still moving.
      blend = Math.min(1, Math.max(0, blend + (wasInside ? 1 : -1) * (dt / INSIDE_BLEND_MS)))

      return { phase, centre, releasePlacement, blend, heading: { ...heading }, entered }
    },

    phase: () => phase,
    hidesSatellites: () => phase !== 'outside',
  }
}
